import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  message,
  Form,
  Input,
  Col,
  Select,
  Modal
} from 'antd';
import { useTranslation } from 'react-i18next';
import {
  SearchOutlined,
  ReloadOutlined,
  EyeOutlined,
  DownloadOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import FilterPanel from '../../components/common/FilterPanel';
import {
  Shelf,
  ShelfFilters,
} from '../../types/shelf';
import shelfService from '../../services/shelfService';
import { warehouseService } from '../../services/warehouseService';
import sectionTypeService from '../../services/sectionTypeService';
import { formatTimestamp } from '../../utils/responseTransformer';
import '../sectionType/sectionType.css';

interface ShelfListProps {
  // 可以添加额外的props
}

const { confirm } = Modal;

const ShelfList: React.FC<ShelfListProps> = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  // 状态管理
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState<Shelf[]>([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0); // 用于手动触发刷新
  const [filters, setFilters] = useState<ShelfFilters>({});
  const [storeOptions, setStoreOptions] = useState<any[]>([]);
  const [sectionTypeOptions, setSectionTypeOptions] = useState<any[]>([]); // 新增状态用于存储sectionType选项
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

   const loadStoreOptions = useCallback(async (searchValue = '') => {
      try {
        const response = await warehouseService.listWarehouse({searchStr:searchValue});
        if (response.success && response.data) {
          setStoreOptions(response.data);
        }
      } catch (error) {
        console.error('获取门店选项失败:', error);
      } finally {
      }
    }, []);

   // 新增方法用于加载sectionType选项
   const loadSectionTypeOptions = useCallback(async (searchValue = '') => {
      try {
        const response = await sectionTypeService.listSectionTypes(searchValue);
        if (response.success && response.data) {
          setSectionTypeOptions(response.data);
        }
      } catch (error) {
        console.error('获取断面类型选项失败:', error);
      }
    }, []);

  // 获取数据
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await shelfService.pageQueryShelves({
        currentPage: pagination.current,
        pageSize: pagination.pageSize,
        warehouseCode: filters.warehouseCode,
        code: filters.code,
        sectionTypeCode: filters.sectionTypeCode,
      });

      if (response.success && response.data) {
        setDataSource(response.data.tableData);
        setPagination((prev: any) => ({
          ...prev,
          total: response.data.paging.totalCount,
        }));
      } else {
        message.error(response.errorMessage || t('shelf.queryFailed'));
      }
    } catch (error) {
      console.error('获取货架列表失败:', error);
      message.error(t('shelf.queryFailed'));
    } finally {
      setLoading(false);
    }
  }, [pagination.current, pagination.pageSize, filters, t]);

  // 初始化和刷新数据
  useEffect(() => {
    fetchData();
  }, [fetchData, refreshTrigger]);

  useEffect(() => {
    loadStoreOptions();
    loadSectionTypeOptions(); // 初始化加载sectionType选项
  }, [loadStoreOptions, loadSectionTypeOptions]);

  // 处理分页
  const handleTableChange = (page: number, size: number) => {
    setPagination((prev: any) => ({
      ...prev,
      current: page,
      pageSize: size,
    }));
  };

  // 处理过滤器变更
  const handleFilterChange = (values: any) => {
    const newFilters = {
      warehouseCode: values.warehouseCode?.trim() || undefined,
      code: values.code?.trim() || undefined,
      sectionTypeCode: values.sectionTypeCode || undefined, // 移除了.trim()，因为Select组件可能返回undefined
    };
    
    setFilters(newFilters);
    setPagination(prev => ({ ...prev, current: 1 }));
    setRefreshTrigger(prev => prev + 1); // 触发刷新
  };

  // 处理重置
  const handleResetFilters = () => {
    setFilters({});
    setPagination(prev => ({ ...prev, current: 1 }));
    setRefreshTrigger(prev => prev + 1); // 触发刷新
  };

  // 查看详情
  const handleView = (record: Shelf) => {
    // 由于是查看功能，我们可以在新页面显示详情，或者使用模态框
    // 这里我们使用模态框显示详情
    navigate(`/shelf/detail/${record.id}`);
  };

  // 刷新数据
  const handleRefresh = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  // 导出货架使用统计
  const handleExportStatistics = () => {
    confirm({
      title: t('shelf.exportConfirm'),
      icon: <ExclamationCircleOutlined />,  
      content: t('shelf.exportStatisticsConfirm'),
      okText: t('common.confirm'),
      cancelText: t('common.cancel'),
      onOk: async () => {
        try {
          const result = await shelfService.exportStatistics();
          if (result.success && result.data) {
            message.success(t('shelf.exportSuccess'));
            // 创建下载链接
            const link = document.createElement('a');
            link.href = result.data.downloadUrl;
            link.download = result.data.fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          } else {
            message.error(result.errorMessage || t('shelf.exportDataFormatError'));
          }
        } catch (error: any) {
          message.error(t('shelf.exportFailed', { error: error.message }));
        }
      },
    });
  };

  // 表格列定义
  const columns: ColumnsType<Shelf> = [
    {
      title: t('shelf.warehouseCode'),
      dataIndex: 'warehouseCode',
      key: 'warehouseCode',
      width: 150,
      fixed: 'left',
    },
    {
      title: t('shelf.code'),
      dataIndex: 'code',
      key: 'code',
      width: 150,
    },
    {
      title: t('shelf.sectionTypeCode'),
      dataIndex: 'sectionTypeCode',
      key: 'sectionTypeCode',
      width: 150,
    },
    {
      title: t('shelf.sectionTypeName'),
      dataIndex: 'sectionTypeName',
      key: 'sectionTypeName',
      width: 150,
    },
    {
      title: t('shelf.outerDimensions'),
      key: 'outerDimensions',
      width: 200,
      render: (_, record) => {
        return `${record.outerLength || '-'} × ${record.outerWidth || '-'} × ${record.outerHeight || '-'}`;
      },
    },
    {
      title: t('shelf.innerDimensions'),
      key: 'innerDimensions',
      width: 200,
      render: (_, record) => {
        return `${record.innerLength || '-'} × ${record.innerWidth || '-'} × ${record.innerHeight || '-'}`;
      },
    },
    {
      title: t('shelf.layerCount'),
      dataIndex: 'layerCount',
      key: 'layerCount',
      width: 100,
    },
    {
      title: t('shelf.displayGroupCode'),
      dataIndex: 'displayGroupCode',
      key: 'displayGroupCode',
      width: 150,
    },
    {
      title: t('shelf.creatorName'),
      dataIndex: 'creatorName',
      key: 'creatorName',
      width: 120,
    },
    {
      title: t('shelf.gmtModified'),
      dataIndex: 'gmtModified',
      key: 'gmtModified',
      width: 180,
      render: (value) => formatTimestamp(value),
    },
    // {
    //   title: t('common.actions'),
    //   key: 'actions',
    //   width: 120,
    //   fixed: 'right',
    //   render: (_, record) => (
    //     <Space size="middle">
    //       <Button 
    //         type="link" 
    //         icon={<EyeOutlined />} 
    //         onClick={() => handleView(record)}
    //         size="small"
    //       >
    //         {t('common.view')}
    //       </Button>
    //     </Space>
    //   ),
    // },
  ];

  return (
    <div>
      <FilterPanel
        title={t("menu.shelf")}
        onFilter={handleFilterChange}
        onReset={handleResetFilters}
    >
        <Col span={6}>
        <Form.Item name="warehouseCode" label={t('shelf.warehouseCode')}>
            <Select
              placeholder={t('shelf.warehouseCodePlaceholder')}
              allowClear
              showSearch
              filterOption={(input, option) => {
                const optionText = option?.children?.toString() || '';
                return optionText.toLowerCase().includes(input.toLowerCase());
              }}
            >
              {storeOptions.map(store => (
                <Select.Option key={store.code} value={store.code}>
                  {store.code} - {store.name}
                </Select.Option>
              ))}
            </Select>
        </Form.Item>
        </Col>
        <Col span={6}>
        <Form.Item name="code" label={t('shelf.code')}>
            <Input placeholder={t('shelf.codePlaceholder')} />
        </Form.Item>
        </Col>
        <Col span={6}>
        <Form.Item name="sectionTypeCode" label={t('shelf.sectionTypeCode')}>
            <Select
              placeholder={t('shelf.sectionTypeCodePlaceholder')}
              allowClear
              showSearch
              filterOption={false} // 我们将使用自定义过滤逻辑
              onSearch={loadSectionTypeOptions} // 搜索时加载选项
              notFoundContent={null}
            >
              {sectionTypeOptions.map(sectionType => (
                <Select.Option key={sectionType.typeCode} value={sectionType.typeCode}>
                  {sectionType.typeCode} - {sectionType.typeName}
                </Select.Option>
              ))}
            </Select>
        </Form.Item>
        </Col>
    </FilterPanel>

      <Card className="content-card">
       

        <div style={{ marginBottom: 16 }}>
          <Button 
            type="primary" 
            icon={<DownloadOutlined />}
            onClick={handleExportStatistics}
          >
            {t('shelf.exportStatistics')}
          </Button>
        </div>
        <Table
          columns={columns}
          dataSource={dataSource}
          loading={loading}
          rowKey="id"
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `${t('common.total')} ${total} ${t('common.items')}`,
            onChange: handleTableChange,
          }}
          scroll={{ x: 1200, y: 500 }}
        />
      </Card>
    </div>
  );
};

export default ShelfList;